// AttendanceNoticeSheet — calm bottom sheet for parents to file an
// "absent" or "running late" notice in 2-3 taps.
//
// Mobile-first. Reused across parent dashboard child-card and the
// dedicated /parent/absences page.

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Calendar, Clock, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  findActiveNotice,
  formatLocalTime,
  formatNoticeDate,
  submitAbsenceNotice,
  submitLateNotice,
} from "@/lib/attendanceNotices";
import { todayStr } from "@/lib/attendance";

const MAX_FUTURE_DAYS = 30;

function maxFutureDate() {
  const d = new Date();
  d.setDate(d.getDate() + MAX_FUTURE_DAYS);
  return d.toISOString().slice(0, 10);
}

export default function AttendanceNoticeSheet({
  open,
  onOpenChange,
  initialType = "absent", // "absent" | "late"
  child,                   // {id, first_name, last_name, family_id, classroom_id, organization_id}
  submittedBy,             // profileId
  onSubmitted,             // (notice) => void
}) {
  const [type, setType] = useState(initialType);
  const [date, setDate] = useState(todayStr());
  const [arrival, setArrival] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  // Reset form whenever the sheet (re)opens.
  useEffect(() => {
    if (!open) return;
    setType(initialType);
    setDate(todayStr());
    setArrival("");
    setReason("");
    setExisting(null);
  }, [open, initialType]);

  // Detect an existing submitted notice for the same (child, date) so
  // we can guide the parent ("you already filed an absence for today").
  useEffect(() => {
    if (!open || !child?.id) return;
    let alive = true;
    findActiveNotice({ childId: child.id, date })
      .then((row) => {
        if (alive) setExisting(row);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, child?.id, date]);

  const childName =
    [child?.first_name, child?.last_name].filter(Boolean).join(" ") || "Child";

  const onSubmit = async () => {
    if (!child?.id) return;
    setBusy(true);
    try {
      const args = {
        childId: child.id,
        familyId: child.family_id,
        classroomId: child.classroom_id || null,
        organizationId: child.organization_id,
        noticeDate: date,
        reason,
        submittedBy,
      };
      const notice =
        type === "late"
          ? await submitLateNotice({ ...args, expectedArrivalTime: arrival || null })
          : await submitAbsenceNotice(args);
      toast.success(
        type === "absent"
          ? `Marked ${childName} absent · ${formatNoticeDate(date)}`
          : `Marked ${childName} running late · ${formatNoticeDate(date)}${
              arrival ? ` (~${formatLocalTime(arrival)})` : ""
            }`,
      );
      onSubmitted?.(notice);
      onOpenChange?.(false);
    } catch (err) {
      // 23505 from the partial unique index = "already filed".
      if (err?.code === "23505") {
        toast.error("You've already filed a notice for this day. Cancel it first to refile.");
      } else {
        toast.error(err?.message || "Could not submit notice");
      }
    } finally {
      setBusy(false);
    }
  };

  const titleCopy =
    type === "absent" ? "Report absence" : "Running late";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="attendance-notice-sheet"
        side="bottom"
        className="rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto sm:rounded-t-3xl"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{titleCopy}</SheetTitle>
          <SheetDescription>
            For <strong className="text-neutral-900">{childName}</strong>.
            Your teacher and the office will see this on their roster
            today.
          </SheetDescription>
        </SheetHeader>

        {/* Type pill toggle */}
        <div
          role="tablist"
          aria-label="Notice type"
          className="mt-5 inline-flex rounded-full bg-neutral-100 p-1"
        >
          <TypeTab
            active={type === "absent"}
            onClick={() => setType("absent")}
            testId="notice-type-absent"
          >
            Absent
          </TypeTab>
          <TypeTab
            active={type === "late"}
            onClick={() => setType("late")}
            testId="notice-type-late"
          >
            Late
          </TypeTab>
        </div>

        {/* Date */}
        <label className="mt-4 block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Date
          </span>
          <div className="relative">
            <Calendar
              size={14}
              weight="duotone"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
              aria-hidden
            />
            <input
              type="date"
              data-testid="notice-date"
              value={date}
              min={todayStr()}
              max={maxFutureDate()}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-9 pr-3 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <p className="mt-1 text-[11px] text-neutral-500">
            {formatNoticeDate(date)} · You can file up to {MAX_FUTURE_DAYS} days
            ahead.
          </p>
        </label>

        {/* Late: expected arrival */}
        {type === "late" && (
          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              Expected arrival (optional)
            </span>
            <div className="relative">
              <Clock
                size={14}
                weight="duotone"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                aria-hidden
              />
              <input
                type="time"
                data-testid="notice-arrival"
                value={arrival}
                onChange={(e) => setArrival(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-9 pr-3 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </label>
        )}

        {/* Reason */}
        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Reason (optional)
          </span>
          <textarea
            rows={2}
            data-testid="notice-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              type === "absent"
                ? "e.g. fever overnight"
                : "e.g. heavy traffic on 95"
            }
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </label>

        {existing && (
          <p
            data-testid="notice-already-filed"
            className="mt-3 rounded-xl border border-amber-300 bg-amber-50/70 px-3 py-2 text-[12px] text-amber-900"
          >
            A notice was already filed for this date. Submitting again
            will fail — open it from the Absences page to cancel first.
          </p>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="notice-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="notice-submit"
            disabled={busy || !!existing}
            onClick={onSubmit}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send notice"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TypeTab({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className={`min-w-[88px] rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${
        active
          ? "bg-white text-neutral-900 shadow-sm"
          : "text-neutral-500 hover:text-neutral-800"
      }`}
    >
      {children}
    </button>
  );
}
